import { StoreApi, UseBoundStore } from 'zustand';

type WithSelectors<S> = S extends { getState: () => infer T } ? S & { use: { [K in keyof T]: () => T[K] } } : never;

/**
 * Create a zustand store selectors automatically
 * Usage example: `const user = useUserStore.use.user();`
 * See: https://zustand.docs.pmnd.rs/guides/auto-generating-selectors
 * 
 * **Note**: Initial state should be set explicit explicitly, otherwise omitting some values may cause an error when 
 * using the "use" syntax
 * 
  ```
  interface UserStoreState {
    user: string;
  }

  // ✅ If you want the user to be initially undefined you need to do this:
  const initialState: UserStoreState = {
    user: undefined,
  };
  
  // ❌ This will cause an error when doing `const user = useUserStore.use.user()` in a component
  const initialState: UserStoreState = {};

  // ...
  const useUserStore = create<UserStoreState>()(() => ({ ...initialState }));
  ```
*/
export const createSelectors = <S extends UseBoundStore<StoreApi<object>>>(_store: S) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    (store.use as any)[k] = () => store((s) => s[k as keyof typeof s]);
  }

  return store;
};
